import { say } from '@/lib/vocabulary';
import { API_ORIGIN } from '@/api/config';

/**
 * The gaps between what the workflow needs and what the backend serves,
 * checked live rather than written down.
 *
 * A list of known gaps kept as prose goes stale the day someone fixes one, and
 * then the product is lying in the other direction - telling an operator that
 * something is missing when it shipped last night. So each gap names the exact
 * request that would prove it closed, and the page asks. What is shown is the
 * backend's own answer, today.
 *
 * Every probe is a read. Nothing here starts a run, writes a record or costs
 * anything, so the page is safe to open at any point in a workflow.
 */

export type GapState = 'open' | 'closed' | 'checking' | 'unknown';

export interface Gap {
  id: string;
  /** What an operator cannot do because of this. */
  title: string;
  /** Why it blocks them, in the product's terms. */
  costs: string;
  /** What the backend would have to add. */
  needs: string;
  /** The request that settles it, relative to the API origin. */
  probe: string;
  /**
   * How to read the answer.
   *
   * `missing-route` - a 404/405 means the route is absent, so the gap is open.
   * `refusal`       - a 501 capability refusal means the gap is open.
   * `payload`       - the route answers, and the payload decides.
   */
  kind: 'missing-route' | 'refusal' | 'payload';
  /** For payload probes: true when the answer shows the gap is still open. */
  stillOpen?: (data: unknown) => boolean;
  /** What the operator sees while the gap is open, in one line. */
  evidence?: (data: unknown) => string | null;
}

/**
 * Written against the routes as they stand. Each `probe` is chosen to be the
 * request an engineer would make to check the same thing by hand.
 */
interface WalkthroughTool {
  sections?: { availability?: string; reason?: string }[];
}

function readTools(d: unknown): WalkthroughTool[] {
  return (d as { tools?: WalkthroughTool[] } | undefined)?.tools ?? [];
}

/** True when at least one section can actually serve a record. */
function supportedSections(d: unknown): boolean {
  return readTools(d).some((t) => (t.sections ?? []).some((s) => s.availability === 'SUPPORTED'));
}

// companyId is kept in the signature because gaps have been company-scoped
// before and will be again; the current set all probe bucket routes.
export function gapsFor(bucketId: string | null, _companyId: string | null): Gap[] {
  const bucket = bucketId ?? '00000000-0000-0000-0000-000000000000';
  return [
    {
      id: 'packaging-run-list',
      title: 'A package cannot be found again',
      costs:
        'Closing the tab strands an in-progress package, and a colleague cannot pick one up. Starting again creates a second run over the same corpus.',
      needs: 'GET /buckets/{bucketId}/packaging-runs, beside the POST that already exists.',
      probe: `/api/v1/buckets/${bucket}/packaging-runs`,
      kind: 'missing-route',
      // Shipped 2026-08-04. Left in place rather than deleted: the page's worth
      // is that it re-asks, and a gap that closed should be seen closing.
    },
    {
      id: 'reconstruction',
      title: 'Files are never turned into records',
      costs:
        'Record preview, search, the relationship graph, recency, liveness and the commercial verdict all read a table that nothing writes.',
      needs: 'A producer that writes reconstructed entities and relationships.',
      probe: `/api/v1/buckets/${bucket}/walkthrough`,
      kind: 'payload',
      // A section existing proves nothing: this bucket returns fifteen of them
      // and every one is UNSUPPORTED. Only a SUPPORTED section means a record
      // can actually be opened, and an earlier version of this check read the
      // mere presence of sections as success and reported the gap closed.
      stillOpen: (d) => !supportedSections(d),
      evidence: (d) => {
        const tools = readTools(d);
        if (tools.length === 0) return 'The walkthrough lists no tools at all.';
        const sections = tools.flatMap((t) => t.sections ?? []);
        const supported = sections.filter((s) => s.availability === 'SUPPORTED').length;
        const reason = sections.find((s) => s.reason)?.reason ?? null;
        return `${tools.length} tools, ${sections.length} sections, ${supported} of them able to serve a record.${
          reason ? ` The backend's reason: ${reason}` : ''
        }`;
      },
    },
    {
      id: 'preview-adapters',
      title: 'No section can serve a record, even where one exists',
      costs:
        'Every walkthrough section reports that no validated preview adapter is registered for it in this bucket, so nothing can be opened and read.',
      needs: 'Registered, validated preview adapters per tool section, and the capability rows that record them.',
      probe: `/api/v1/buckets/${bucket}/walkthrough`,
      kind: 'payload',
      stillOpen: (d) => !supportedSections(d),
      evidence: (d) => {
        const sections = readTools(d).flatMap((t) => t.sections ?? []);
        const unsupported = sections.filter((s) => s.availability !== 'SUPPORTED').length;
        return `${unsupported} of ${sections.length} sections have no adapter registered.`;
      },
    },
    {
      id: 'promotion',
      title: 'No tool can be marked proven from the product',
      costs:
        'A detection reaches the evidence bar and stops there. Promotion is what turns a candidate into a supported tool, and a supported tool is what a placement rule keys on, so nothing can be placed and nothing can ship.',
      // The bar was five evidence families against a rule set that produces
      // four, which made promotion unreachable by arithmetic. It is two now,
      // so the arithmetic works and the missing piece moved: the database has
      // promote_tool_detection and no route calls it.
      needs:
        'A route over promote_tool_detection, which exists in the database and checks the reviewer role and the evidence bar itself.',
      probe: '/api/v1/attribution/rules',
      kind: 'payload',
      stillOpen: (d) => {
        const items = (d as { items?: { method?: string }[] } | undefined)?.items ?? [];
        const methods = new Set(items.map((i) => i.method).filter(Boolean));
        // Two distinct kinds of check is the recalibrated bar. Below it the
        // evidence cannot reach the standard whatever else is built.
        return methods.size < 2;
      },
      evidence: (d) => {
        const items = (d as { items?: { method?: string }[] } | undefined)?.items ?? [];
        const methods = [...new Set(items.map((i) => i.method).filter(Boolean))];
        return `${items.length} rules across ${methods.length} kinds of check: ${methods
          .map((m) => say(String(m)))
          .join(', ')}. Two are required, so the evidence bar is reachable and the promotion step is what is missing.`;
      },
    },
    {
      id: 'promote-route',
      title: 'A reviewer cannot promote a detection from the product',
      costs:
        'Promotion is a human decision with a role behind it, and the only way to make it is a database session. An operator holding the evidence on screen cannot act on it.',
      needs:
        'A route over promote_tool_detection. It is written, authorised, and enforces the evidence bar itself, so the route is the whole of it.',
      probe: `/api/v1/buckets/${bucket}/attribution/promotions`,
      kind: 'missing-route',
    },
    {
      id: 'classification',
      title: 'Almost nothing is traced to a tool, so almost nothing can be placed',
      costs:
        'A placement rule keys on a tool detection. A file no rule recognises gets a reason instead of a folder and ships under _stray by that reason, so an untraced corpus is delivered in full but arrives sorted by why it could not be traced rather than by the software that made it.',
      needs:
        'Classification by inference rather than a fixed rule table. The content is already read and stored; the step that names what it is does not exist.',
      probe: `/api/v1/buckets/${bucket}/attribution/stray`,
      kind: 'payload',
      // The strays are the evidence and they are readable, so this one is
      // probed rather than stated. Any stray at all leaves files unplaceable.
      stillOpen: (d) => ((d as { items?: unknown[] } | undefined)?.items ?? []).length > 0,
      evidence: (d) => {
        const items = (d as { items?: { reason?: string }[] } | undefined)?.items ?? [];
        if (items.length === 0) return 'No stray objects on this page.';
        const reason = items.find((i) => i.reason)?.reason ?? null;
        return `${items.length} objects on the first page alone are traced to no tool${
          reason ? `, the first because ${say(String(reason))}` : ''
        }. Each one is a file that cannot be placed, and so a file that cannot ship.`;
      },
    },
    /*
     * `manifest-browse` stood here claiming "the delivered file list cannot be
     * opened in the product", with a probe pinned to a zero UUID so it could
     * never resolve and reported "not checked" forever. The product opens it:
     * `DeliveredFiles` pages `getPackagingRunManifest` under "Every file that
     * ships, one by one" on the packaging page. A known-gaps list that names a
     * shipped feature as missing is worse than a shorter list.
     */
    {
      id: 'operations-feed',
      title: 'Runs cannot be watched in one place',
      costs: 'There is no single view of what is happening across every bucket.',
      needs: 'The operations feed, answering rather than refusing.',
      probe: '/api/v1/operations',
      // Was a typed refusal; it is a real route now, so anything but a success
      // means it exists and cannot serve rather than that it declines to.
      kind: 'missing-route',
    },
    {
      id: 'chat',
      title: 'The corpus cannot be asked questions',
      costs: 'No way to ask what is in a corpus and get answers cited back to files.',
      needs: 'The chat routes, which currently answer with a capability refusal.',
      probe: `/api/v1/buckets/${bucket}/chat/sessions`,
      kind: 'refusal',
    },
    {
      id: 'domain-graph',
      title: 'The corpus cannot be seen as domains',
      costs:
        'Without it, a bucket is a list of tools and a pile of untraced files, with nothing saying what the data actually is.',
      needs: 'GET /buckets/{bucketId}/domain-graph, grouping record shapes by the fields they share.',
      probe: `/api/v1/buckets/${bucket}/domain-graph`,
      kind: 'payload',
      stillOpen: (d) => {
        const domains = (d as { domains?: unknown[] } | undefined)?.domains ?? [];
        return domains.length === 0;
      },
      evidence: (d) => {
        const x = d as { domains?: { objectCount?: number; shapeCount?: number }[]; nodes?: unknown[] } | undefined;
        const domains = x?.domains ?? [];
        if (domains.length === 0) return 'The route answers, and finds no domains in this bucket.';
        const files = domains.reduce((n, y) => n + (y.objectCount ?? 0), 0);
        return `${domains.length} domains over ${files.toLocaleString()} files, from ${(x?.nodes ?? []).length} distinct record shapes.`;
      },
    },
    {
      id: 'schema-clusters',
      title: 'Unrecognised files are one undifferentiated pile',
      costs:
        'Most files in a large bucket match no rule. They can be grouped by their shape, which turns "98% untraced" into a handful of named clusters a person can confirm.',
      needs: 'The cluster store wired for this bucket, so the route can answer.',
      probe: `/api/v1/buckets/${bucket}/schema-clusters`,
      kind: 'missing-route',
    },
  ];
}

export interface GapResult {
  gap: Gap;
  state: GapState;
  status: number | null;
  evidence: string | null;
}

/**
 * Runs one probe. Reads only; never writes, never costs anything.
 *
 * A gap is closed only when the probe actually succeeds. Existence is not
 * enough and this has now been the wrong answer three times: a route that
 * answers 503 on every call, a section list where every section is
 * UNSUPPORTED, and a walkthrough whose sections exist but serve nothing. Each
 * looked finished to a check that asked the easier question.
 */
export async function checkGap(gap: Gap): Promise<GapResult> {
  // A probe that needs an id it does not have would answer 404 for the nil
  // UUID and be read as a missing route, which is how two shipped routes were
  // reported permanently absent. Not checked is its own answer.
  if (gap.probe.includes('00000000-0000-0000-0000-000000000000')) {
    return {
      gap,
      state: 'unknown',
      status: null,
      evidence: 'Not checked: this probe needs a bucket or company, and none is selected.',
    };
  }
  try {
    const res = await fetch(`${API_ORIGIN}${gap.probe}`, { credentials: 'include' });
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      body = null;
    }
    const data = (body as { data?: unknown } | null)?.data ?? null;
    const code = (body as { error?: { code?: string } } | null)?.error?.code ?? null;

    // Anything that is not a success means the workflow still cannot use this,
    // whatever the reason. A 405 says the route is absent, a 501 says it
    // refuses, a 503 says its dependency is down - all of them are open, and
    // the status is shown so the difference is visible.
    if (!res.ok) {
      const why =
        res.status === 404 || res.status === 405
          ? 'The route is not registered.'
          : res.status === 501 || code === 'CAPABILITY_UNAVAILABLE'
            ? 'The route answers with a capability refusal.'
            : `The route exists but answered ${res.status}${code ? ` (${code})` : ''}, so nothing can use it.`;
      return { gap, state: 'open', status: res.status, evidence: why };
    }

    if (gap.kind !== 'payload') {
      return { gap, state: 'closed', status: res.status, evidence: null };
    }
    if (data === null) {
      return { gap, state: 'unknown', status: res.status, evidence: 'The route answered without a payload.' };
    }
    const open = gap.stillOpen ? gap.stillOpen(data) : false;
    return { gap, state: open ? 'open' : 'closed', status: res.status, evidence: gap.evidence?.(data) ?? null };
  } catch {
    return { gap, state: 'unknown', status: null, evidence: 'The request could not be made.' };
  }
}
